import { useMutation, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import type { GetThreadResponse, ListThreadsResponse } from "@onemileauto/shared-types";
import { setThreadUnread } from "../lib/api-client";
import { mailboxQueryKeys } from "../lib/query-keys";

/**
 * Opening a thread marks it read. Optimistically flips `unread` everywhere
 * it's cached (every folder's thread list that happens to include this
 * thread, plus the open thread-detail view) so the unread dot clears
 * instantly instead of waiting on a round trip.
 */
export function useSetThreadUnread(mailboxId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ threadId, unread }: { threadId: string; unread: boolean }) =>
      setThreadUnread(mailboxId!, threadId, unread),
    onMutate: async ({ threadId, unread }) => {
      if (!mailboxId) return;
      const listKey = mailboxQueryKeys.threadsAll(mailboxId);
      const detailKey = mailboxQueryKeys.thread(mailboxId, threadId);

      await queryClient.cancelQueries({ queryKey: listKey });
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousLists = queryClient.getQueriesData<InfiniteData<ListThreadsResponse>>({ queryKey: listKey });
      const previousDetail = queryClient.getQueryData<GetThreadResponse>(detailKey);

      queryClient.setQueriesData<InfiniteData<ListThreadsResponse>>({ queryKey: listKey }, (data) =>
        data
          ? {
              ...data,
              pages: data.pages.map((page) => ({
                ...page,
                threads: page.threads.map((t) => (t.id === threadId ? { ...t, unread } : t)),
              })),
            }
          : data,
      );

      if (previousDetail) {
        queryClient.setQueryData<GetThreadResponse>(detailKey, {
          thread: { ...previousDetail.thread, unread },
        });
      }

      return { previousLists, previousDetail, detailKey };
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      for (const [key, data] of context.previousLists) {
        queryClient.setQueryData(key, data);
      }
      if (context.previousDetail) {
        queryClient.setQueryData(context.detailKey, context.previousDetail);
      }
    },
  });
}
